import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, FileText, Edit3, CheckCircle, AlertTriangle, Clock, Loader2, Sparkles, Info, Plus, ZoomIn, ZoomOut, Maximize, RotateCw, LayoutGrid, Zap } from "lucide-react";
import { toast } from "sonner";
import { WorkflowTimer } from "@/components/WorkflowTimer";
import StyleSelector from "@/components/StyleSelector";
import { SmartCorrectionPanel as _UnusedSmartCorrectionPanel } from "@/components/SmartCorrectionPanel";  // kept for possible re-enable

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const showBillingToast = (billing) => {
  if (!billing) return;

  console.log("💳 Billing payload:", billing);

  if (billing.charged === true) {
    const amount = billing?.transaction?.amount ?? billing?.amount ?? 1;
    toast.success(`Wallet charged — ${amount} credit${Number(amount) === 1 ? '' : 's'} deducted`);
    return;
  }

  if (billing.billing_status === "already_charged") {
    toast.info("Already paid — regeneration is free");
    return;
  }

  if (billing.billing_status === "skipped_non_production") {
    console.info("Wallet skipped in test/non-production");
    return;
  }

  if (billing.billing_status === "error") {
    toast.warning(billing.message || "Opinion generated, but wallet billing needs review");
  }
};

const getFormatKey = (format) => {
  if (!format) return null;
  if (typeof format === "string") return format;
  return format.id || format.format_id || format.code || format.name || null;
};

const REVIEW_STAGES = {
  READY_FOR_FORMAT_SELECTION: 'ready_for_format_selection',
  GENERATING_OPINION: 'generating_opinion'
};

const getPersistedWorkflowState = (extractionRecord) => {
  const persistedFormat = getFormatKey(extractionRecord?.selected_format);
  const persistedWorkflowType = typeof extractionRecord?.workflow_type === 'string'
    ? extractionRecord.workflow_type.trim().toLowerCase()
    : '';
  const hasPersistedWorkflow = Boolean(persistedFormat && persistedWorkflowType);
  const persistedHcgRequired = hasPersistedWorkflow
    ? Boolean(extractionRecord?.hcg_required)
    : false;

  return {
    persistedFormat,
    persistedWorkflowType,
    persistedHcgRequired,
    hasPersistedWorkflow,
  };
};

// Format duration in ms to readable string
const formatDuration = (ms) => {
  if (!ms || ms < 0) return '0s';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
};

// Live timer component for generation
const GenerationTimer = ({ startTime }) => {
  const [elapsed, setElapsed] = useState(0);
  
  useEffect(() => {
    if (!startTime) return;
    const updateElapsed = () => setElapsed(Date.now() - startTime);
    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [startTime]);
  
  return (
    <span className="font-mono font-bold text-white ml-2">
      {formatDuration(elapsed)}
    </span>
  );
};

const ExtractPage = () => {
  const { extractionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [extractedData, setExtractedData] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [correctedDocuments, setCorrectedDocuments] = useState([]);  // V2: Store corrected documents from Human Correction Gate
  const [bankFormats, setBankFormats] = useState([]);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [selectedStyleId, setSelectedStyleId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [workflowType, setWorkflowType] = useState("");
  const [hcgRequired, setHcgRequired] = useState(false);
  const [formatPersisting, setFormatPersisting] = useState(false);
  const [reviewReady, setReviewReady] = useState(false);
  const [hcgContract, setHcgContract] = useState({
    status: null,
    isApproved: false,
    corrected_documents_hash: null,
    corrected_documents_version: null,
    approved_at: null,
    approved_by: null,
    documents_count: 0,
    exceptions: [],
    reviewer_confirmation: false,
    isDirty: false,
  });
  const [extractionTiming, setExtractionTiming] = useState(null);
  const [generationStartTime, setGenerationStartTime] = useState(null);
  const [generatedOpinionsByFormat, setGeneratedOpinionsByFormat] = useState({});
  const [activeGeneratedOpinion, setActiveGeneratedOpinion] = useState(null);
  const [regenerationAvailable, setRegenerationAvailable] = useState(false);
  
  // Format restriction state
  const [formatRestrictions, setFormatRestrictions] = useState(null);
  const [restrictionShown, setRestrictionShown] = useState(false);
  const [showLookbackChoiceModal, setShowLookbackChoiceModal] = useState(false);
  const [lookbackAcknowledged, setLookbackAcknowledged] = useState(false);
  const [pendingFormatSelection, setPendingFormatSelection] = useState("");
  const [uiStage, setUiStage] = useState(location.state?.workflowStage || REVIEW_STAGES.READY_FOR_FORMAT_SELECTION);
  const [uiStageMessage, setUiStageMessage] = useState(location.state?.stageMessage || 'Extraction complete. Select a format to continue.');

  useEffect(() => {
    const resetStorageKey = extractionId ? `extract-return-reset:${extractionId}` : null;
    const shouldResetFromStorage = resetStorageKey ? sessionStorage.getItem(resetStorageKey) === '1' : false;
    const shouldReset = Boolean(location.state?.resetFormatSelectionOnReturn || shouldResetFromStorage);

    if (!shouldReset) return;

    setSelectedFormat(null);
    setWorkflowType('');
    setHcgRequired(false);
    setReviewReady(false);
    setActiveGeneratedOpinion(null);
    setRegenerationAvailable(false);
    setUiStage(REVIEW_STAGES.READY_FOR_FORMAT_SELECTION);
    setUiStageMessage('Extraction complete. Select a format to continue.');

    if (resetStorageKey) {
      sessionStorage.removeItem(resetStorageKey);
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [extractionId, location.pathname, location.state, navigate]);

  useEffect(() => {
    setCorrectedDocuments([]);
    setReviewReady(false);
    setHcgContract({
      status: null,
      isApproved: false,
      corrected_documents_hash: null,
      corrected_documents_version: null,
      approved_at: null,
      approved_by: null,
      documents_count: 0,
      exceptions: [],
      reviewer_confirmation: false,
      isDirty: false,
    });
  }, [extractionId]);

  useEffect(() => {
    let isMounted = true;
    
    // Fetch format restrictions based on lookback period
    const fetchFormatRestrictions = async () => {
      try {
        const response = await axios.get(`${API}/extraction/${extractionId}/format-restrictions`);
        if (isMounted) {
          setFormatRestrictions(response.data);
          
          // Show info toast once if lookback was detected
          if (response.data.lookback_years > 0 && !restrictionShown) {
            const msg = response.data.requires_user_choice
              ? `Document span: ${response.data.lookback_years} years. Retail format will be limited to 31 years.`
              : `Examination period: ${response.data.lookback_years} years detected.`;
            toast.info(msg, { duration: 5000 });
            setRestrictionShown(true);
          }
        }
      } catch (error) {
        console.error("Could not fetch format restrictions:", error);
        // Don't block on this error - allow all formats if restriction check fails
      }
    };
    
    const fetchBankFormats = async () => {
      try {
        console.log(`🔍 Fetching ALL bank formats (Retail + APF + Scrutiny)`);
        // Authorization header is automatically added by axios interceptor in @/lib/axios
        
        const [retailRes, apfRes, scrutinyRes] = await Promise.all([
          axios.get(`${API}/bank-formats?category=RETAIL`),
          axios.get(`${API}/bank-formats?category=APF`),
          axios.get(`${API}/bank-formats?category=SCRUTINY`)
        ]);
        
        const retailFormats = retailRes.data.formats || [];
        const apfFormats = apfRes.data.formats || [];
        const scrutinyFormats = scrutinyRes.data.formats || [];
        const allFormats = [...retailFormats, ...apfFormats, ...scrutinyFormats];
        
        console.log(`✅ Total formats: ${allFormats.length} (${retailFormats.length} Retail + ${apfFormats.length} APF + ${scrutinyFormats.length} Scrutiny)`);
        if (isMounted) setBankFormats(allFormats);
      } catch (error) {
        console.error("❌ Error fetching bank formats:", error);
      }
    };

    const fetchExtractedData = async () => {
      try {
        console.log(`🔍 Fetching extraction data for ID: ${extractionId}`);
        const response = await axios.get(`${API}/extracted-data/${extractionId}`);
        console.log(`✅ Extraction response:`, response.data);
        const data = response.data.data;
        
        if (!isMounted) return;
        
        setExtractedData(data);

        const {
          persistedFormat,
          persistedWorkflowType,
          persistedHcgRequired,
          hasPersistedWorkflow,
        } = getPersistedWorkflowState(data);

        console.log(`📌 Workflow type from data: ${data.workflow_type || 'not selected yet'}`);
        setSelectedFormat(persistedFormat || null);
        setWorkflowType(hasPersistedWorkflow ? persistedWorkflowType : "");
        setHcgRequired(hasPersistedWorkflow ? persistedHcgRequired : false);
        setReviewReady(false);
        setCorrectedDocuments([]);
        setHcgContract({
          status: null,
          isApproved: false,
          corrected_documents_hash: null,
          corrected_documents_version: null,
          approved_at: null,
          approved_by: null,
          documents_count: 0,
          exceptions: [],
          reviewer_confirmation: false,
          isDirty: false,
        });
        setActiveGeneratedOpinion(null);
        setRegenerationAvailable(false);
        setUiStage(REVIEW_STAGES.READY_FOR_FORMAT_SELECTION);
        setUiStageMessage(
          hasPersistedWorkflow
            ? (persistedWorkflowType === 'apf'
                ? `Persisted ${persistedFormat} APF workflow restored. You can generate the APF opinion directly.`
                : `Persisted ${persistedFormat} retail workflow restored. Review and approve HCG before generating.`)
            : 'Extraction complete. Select a format to continue.'
        );
        
        await fetchBankFormats();
        
        const extractedFields = data.data || {};
        
        // Helper: returns first non-empty string from a list of values
        const firstNonEmpty = (...values) =>
          values.find(v => typeof v === 'string' && v.trim() !== '') || '';
        
        // Extended fallback chain for field resolution
        // Supports multiple document types: Sale Deed, Gift Deed, Mortgage, etc.
        const fieldsWithDefaults = {
          'BorrowerName': firstNonEmpty(
            extractedFields.BorrowerName,
            extractedFields.mortgager_name,
            extractedFields.title_holder_name,
            extractedFields.applicant_name,
            extractedFields['Gift Deed_1_ Buyer'],
            extractedFields['Sale Deed_1_ Buyer']
          ),
          'PropertyDescription': firstNonEmpty(
            extractedFields.PropertyDescription,
            extractedFields.property_description,
            extractedFields['Gift Deed_1_ Schedule Of Property'],
            extractedFields['Sale Deed_1_ Schedule Of Property'],
            extractedFields.property_address
          ),
          ...extractedFields
        };
        
        if (isMounted) {
          setEditedData(fieldsWithDefaults);
          setLoading(false);
          
          // Fetch format restrictions after data is loaded
          fetchFormatRestrictions();
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to load extracted data");
        navigate("/");
      }
    };

    fetchExtractedData();
    
    return () => { isMounted = false; };
  }, [extractionId, navigate, restrictionShown]);

  const selectedFormatKey = useMemo(() => getFormatKey(selectedFormat), [selectedFormat]);

  const generatedOpinionForSelectedFormat = useMemo(() => {
    if (!selectedFormatKey) return null;
    return generatedOpinionsByFormat[selectedFormatKey] || generatedOpinionsByFormat[selectedFormat] || null;
  }, [generatedOpinionsByFormat, selectedFormatKey, selectedFormat]);

  useEffect(() => {
    if (!selectedFormatKey) {
      setActiveGeneratedOpinion(null);
      setRegenerationAvailable(false);
      return;
    }

    const existingOpinion =
      generatedOpinionsByFormat?.[selectedFormatKey] ||
      generatedOpinionsByFormat?.[selectedFormat];

    if (existingOpinion?.document_id || existingOpinion?.download_url) {
      setActiveGeneratedOpinion(existingOpinion);
      setRegenerationAvailable(true);
      setUiStage(REVIEW_STAGES.READY_FOR_FORMAT_SELECTION);
      return;
    }

    setActiveGeneratedOpinion(null);
    setRegenerationAvailable(false);
  }, [selectedFormat, selectedFormatKey, generatedOpinionsByFormat]);

  useEffect(() => {
    const fetchExistingOpinionForFormat = async () => {
      if (!extractionId || !selectedFormatKey) return;
      try {
        const response = await axios.get(`${API}/opinions/my-opinions`);
        const opinions = response.data?.opinions || [];
        const matchingOpinion = opinions.find((opinion) => {
          const originalExtractionId = opinion.original_extraction_id || opinion.extraction_id;
          return originalExtractionId === extractionId && getFormatKey(opinion.bank_format) === selectedFormatKey;
        });

        if (!matchingOpinion) {
          setGeneratedOpinionsByFormat((prev) => {
            if (!prev[selectedFormatKey] && !prev[selectedFormat]) return prev;
            const next = { ...prev };
            delete next[selectedFormatKey];
            delete next[selectedFormat];
            return next;
          });
          setActiveGeneratedOpinion(null);
          setRegenerationAvailable(false);
          return;
        }

        const existingOpinion = {
          extraction_id: matchingOpinion.extraction_id,
          original_extraction_id: matchingOpinion.original_extraction_id || matchingOpinion.extraction_id,
          bank_format: matchingOpinion.bank_format,
          document_id: matchingOpinion.id,
          docx_path: matchingOpinion.docx_path || "",
          pdf_path: matchingOpinion.pdf_path || "",
          status: matchingOpinion.status || 'draft',
          verification_status: matchingOpinion.verification_status ?? null,
          export_warning: matchingOpinion.export_warning ?? null,
          generated_at: matchingOpinion.generated_at,
        };

        setGeneratedOpinionsByFormat((prev) => ({
          ...prev,
          [selectedFormatKey]: existingOpinion,
        }));
        setActiveGeneratedOpinion(existingOpinion);
        setRegenerationAvailable(true);
        setUiStage(REVIEW_STAGES.READY_FOR_FORMAT_SELECTION);
        setUiStageMessage(
          `A ${selectedFormatKey} opinion already exists${existingOpinion.generated_at ? ` (generated ${new Date(existingOpinion.generated_at).toLocaleString()})` : ''}. You can continue to signature or regenerate it.`
        );
      } catch (error) {
        console.error('Failed to fetch existing opinions for format:', error);
      }
    };

    fetchExistingOpinionForFormat();
  }, [API, extractionId, selectedFormat, selectedFormatKey]);
  
  // Check if selected format is Retail and needs capping confirmation
  const isRetailFormat = (formatId) => {
    const format = bankFormats.find(f => f.id === formatId);
    return format?.category === 'RETAIL';
  };

  const resolveWorkflowFromFormat = (formatId) => {
    const format = bankFormats.find(f => f.id === formatId);
    if (format?.category === 'APF') return 'apf';
    return formatId?.toLowerCase().includes('apf') ? 'apf' : 'retail';
  };

  const persistFormatSelection = async (formatId) => {
    if (!formatId) return;

    setFormatPersisting(true);

    try {
      const response = await axios.post(`${API}/extraction/${extractionId}/select-format`, {
        selected_format: formatId,
      });

      setSelectedFormat(response.data.selected_format || null);
      setWorkflowType(response.data.workflow_type);
      setHcgRequired(response.data.hcg_required);
      setReviewReady(false);
      setCorrectedDocuments([]);
      const persistedFormatKey = getFormatKey(response.data.selected_format);
      setActiveGeneratedOpinion(generatedOpinionsByFormat[persistedFormatKey] || generatedOpinionsByFormat[response.data.selected_format] || null);
      setGenerating(false);
      setGenerationStartTime(null);
      setUiStage(REVIEW_STAGES.READY_FOR_FORMAT_SELECTION);
      setUiStageMessage(
        generatedOpinionsByFormat[persistedFormatKey] || generatedOpinionsByFormat[response.data.selected_format]
          ? 'A generated opinion already exists for this format. You can continue to signature or regenerate it.'
          : 'Format selected. Generate the opinion to continue.'
      );

      if (response.data.workflow_type === 'apf') {
        setCorrectedDocuments([]);
        if (!(generatedOpinionsByFormat[persistedFormatKey] || generatedOpinionsByFormat[response.data.selected_format])) {
          toast.success('APF format selected. Human Correction Gate is skipped.');
        }
      } else {
        if (!(generatedOpinionsByFormat[persistedFormatKey] || generatedOpinionsByFormat[response.data.selected_format])) {
          toast.success('Retail format selected. Review documents in HCG before generating.');
        }
      }
    } catch (error) {
      console.error('Failed to persist format selection:', error);
      setSelectedFormat(null);
      setWorkflowType('');
      setHcgRequired(false);
      setReviewReady(false);
      toast.error('Failed to save format selection. Please try again.');
    } finally {
      setFormatPersisting(false);
    }
  };
  
  // Handle format selection - show modal if Retail selected with >31 year lookback
  const handleFormatSelect = async (formatId) => {
    setSelectedFormat(formatId);
    setPendingFormatSelection(formatId);
    setReviewReady(false);
    const formatKey = getFormatKey(formatId);
    const existingOpinion = generatedOpinionsByFormat[formatKey] || generatedOpinionsByFormat[formatId] || null;
    setActiveGeneratedOpinion(existingOpinion);
    setRegenerationAvailable(Boolean(existingOpinion?.document_id || existingOpinion?.download_url));
    setUiStage(REVIEW_STAGES.READY_FOR_FORMAT_SELECTION);
    setUiStageMessage(
      existingOpinion
        ? 'A generated opinion already exists for this format. You can continue to signature or regenerate it.'
        : 'Format selected. Generate the opinion to continue.'
    );
    
    // Check if this requires user acknowledgment
    if (formatRestrictions?.requires_user_choice && isRetailFormat(formatId) && !lookbackAcknowledged) {
      setShowLookbackChoiceModal(true);
      return;
    }

    await persistFormatSelection(formatId);
  };
  
  // Handle user's choice in the lookback modal
  const handleLookbackChoice = async (choice) => {
    if (choice === 'retail') {
      setLookbackAcknowledged(true);
      setShowLookbackChoiceModal(false);
      const formatToPersist = pendingFormatSelection;
      setPendingFormatSelection("");
      if (formatToPersist) {
        setTimeout(() => {
          persistFormatSelection(formatToPersist);
        }, 0);
      }
      toast.success(`Retail format selected. Opinion will cover the most recent 31 years.`);
    } else if (choice === 'apf') {
      // Find and select an APF format
      const apfFormat = bankFormats.find(f => f.category === 'APF');
      if (apfFormat) {
        setPendingFormatSelection("");
        setSelectedFormat(apfFormat.id);
        setTimeout(() => {
          persistFormatSelection(apfFormat.id);
        }, 0);
        toast.success(`Switched to APF format. Full ${formatRestrictions.lookback_years} year examination.`);
      }
      setShowLookbackChoiceModal(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedData({ ...editedData, [field]: value });
  };

  const handleGenerateOpinion = async () => {
    // Prevent multiple simultaneous calls
    if (generating) {
      console.warn('⚠️ Generation already in progress, ignoring duplicate call');
      return;
    }

    if (!selectedFormat) {
      toast.error("Please select a bank format");
      return;
    }

    // VALIDATION: Both fields are now OPTIONAL
    // PropertyDescription auto-fills from Sale Deed extent or constructed from property details
    // BorrowerName auto-fills from Sale Deed buyer
    // No mandatory validation - system has smart fallbacks for both fields
    
    // Optional: Show warning if critical data seems to be missing
    const warnings = [];
    
    const hasSaleDeed = Object.keys(editedData).some(key => 
      (key.includes('SaleDeed') || key.includes('Sale Deed')) && 
      editedData[key]
    );
    
    const hasPropertyDetails = Object.keys(editedData).some(key =>
      (key.includes('plot') || key.includes('survey') || 
       key.includes('Extent') || key.includes('Boundary')) &&
      editedData[key]
    );
    
    // Warn if no property description AND no fallback data
    if (!editedData['PropertyDescription']?.trim() && !hasSaleDeed && !hasPropertyDetails) {
      warnings.push('No property description or property details found - document may be incomplete');
    }
    
    // Warn if no borrower name AND no sale deed
    if (!editedData['BorrowerName']?.trim() && !hasSaleDeed) {
      warnings.push('No borrower name or Sale Deed found - owner name may be missing');
    }
    
    // Show warnings but don't block (user can proceed)
    if (warnings.length > 0) {
      console.warn('Validation warnings:', warnings);
      // Don't block - just log for debugging
    }

    setGenerating(true);
    setGenerationStartTime(Date.now());
    setUiStage(REVIEW_STAGES.GENERATING_OPINION);
    setUiStageMessage('Format selected. Generating opinion now.');

    try {
      if (shouldShowHCG && (!reviewReady || !hcgContract.isApproved || hcgContract.isDirty)) {
        toast.error('Save and approve the Human Correction Gate before generating the opinion.');
        setGenerating(false);
        setGenerationStartTime(null);
        setUiStage(REVIEW_STAGES.READY_FOR_FORMAT_SELECTION);
        setUiStageMessage('Save and approve the Human Correction Gate before generating.');
        return;
      }

      const safeCorrectedDocuments = shouldShowHCG && reviewReady ? correctedDocuments : [];
      const useApprovedHcgSnapshotServerSide = Boolean(
        shouldShowHCG &&
        reviewReady &&
        hcgContract.isApproved &&
        !hcgContract.isDirty &&
        hcgContract.corrected_documents_hash &&
        hcgContract.corrected_documents_version
      );
      console.log('📤 Sending generate-opinion request...');
      console.log('🎨 Using style_id:', selectedStyleId || 'none');
      console.log(
        '📄 Sending corrected_documents:',
        useApprovedHcgSnapshotServerSide ? 0 : (safeCorrectedDocuments?.length || 0),
        'documents'
      );
      const requestPayload = {
        extracted_data_id: extractionId,
        original_extraction_id: extractionId,
        bank_format: selectedFormat,
        edited_data: editedData,
        style_id: selectedStyleId || null,  // Pass advocate style
        corrected_documents_hash: hcgContract.corrected_documents_hash,
        corrected_documents_version: hcgContract.corrected_documents_version,
      };
      if (!useApprovedHcgSnapshotServerSide) {
        requestPayload.corrected_documents = safeCorrectedDocuments;
      }
      const response = await axios.post(`${API}/generate-opinion`, requestPayload, {
        timeout: 300000  // 5 minutes timeout for Claude-powered generation
      });
      
      const documentId = response.data.document_id;
      console.log('✅ Opinion generated successfully:', documentId);
      toast.success("Opinion generated successfully!");
      setTimeout(() => {
        showBillingToast(response.data?.billing);
      }, 350);
      const generatedResult = {
        extraction_id: extractionId,
        original_extraction_id: extractionId,
        bank_format: selectedFormatKey || selectedFormat,
        document_id: documentId,
        docx_path: response.data?.docx_path || "",
        pdf_path: response.data?.pdf_path || "",
        status: response.data?.status || 'draft',
        verification_status: response.data?.verification_status ?? null,
        export_warning: response.data?.export_warning ?? null,
        billing: response.data?.billing || null,
      };
      setGeneratedOpinionsByFormat(prev => ({
        ...prev,
        [selectedFormatKey || selectedFormat]: generatedResult,
      }));
      setActiveGeneratedOpinion(generatedResult);
      setRegenerationAvailable(true);
      setTimeout(() => {
        setGenerating(false);
        setGenerationStartTime(null);
        sessionStorage.setItem(`extract-return-reset:${extractionId}`, '1');
        navigate(`/signature/${documentId}`, {
          state: {
            extractionId,
            resetFormatSelectionOnReturn: true,
          }
        });
      }, 650);
    } catch (error) {
      console.error('❌ Generation error:', error);
      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        'Opinion generation failed';
      
      // Handle format restriction error
      if (error.response?.data?.error === 'format_restriction') {
        const detail = error.response.data;
        toast.error(
          `Format Restriction: ${detail.message || 'Retail format not allowed for this examination period.'}`,
          { duration: 10000 }
        );
      } else {
        toast.error(detail);
      }
      
      setGenerating(false);
      setGenerationStartTime(null);
      setUiStage(REVIEW_STAGES.READY_FOR_FORMAT_SELECTION);
      setUiStageMessage('Generation stopped. You can adjust the format or try again.');
    }
    // Note: Don't set generating=false on success, navigation will unmount component
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading extracted data...</p>
        </div>
      </div>
    );
  }

  const fieldLabels = {
    'BorrowerName': 'Borrower Name',
    'BorrowerAddress': 'Borrower Address',
    'TitleHolderName': 'Title Holder Name',
    'TitleHolderAddress': 'Title Holder Address',
    'TitleHolderConstitution': 'Title Holder Constitution',
    'PropertyDescription': 'Property Description',
    'Boundary_North': 'North Boundary',
    'Boundary_South': 'South Boundary',
    'Boundary_East': 'East Boundary',
    'Boundary_West': 'West Boundary',
    'PropertyNature': 'Property Nature (Freehold/Leasehold)',
    'PropertyType': 'Property Type',
    'LandType': 'Land Type',
    'PermittedUsage': 'Permitted Usage',
    'SurveyNumber': 'Survey Number',
    'Village': 'Village/Locality',
    'District': 'District',
    'RegistrationDate': 'Registration Date',
    'RegistrationNumber': 'Registration Number',
    'Possession': 'Possession Status',
    'Encumbrances': 'Encumbrances',
    'TaxDetails': 'Tax Details',
    'ECPeriod': 'EC Period',
    'Litigation': 'Litigation',
    'MinorInterest': "Minor's Interest"
  };

  const hasSelectedFormat = Boolean(selectedFormat);
  const shouldShowHCG = hasSelectedFormat && workflowType === 'retail' && hcgRequired;
  const shouldShowAPFDirectFlow = hasSelectedFormat && workflowType === 'apf';
  const canGenerate = hasSelectedFormat && !formatPersisting && !generating && (!shouldShowHCG || (reviewReady && hcgContract.isApproved && !hcgContract.isDirty));
  const canContinueToSignature = canGenerate;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                data-testid="back-button"
                variant="ghost"
                onClick={() => navigate("/")}
                className="hover:bg-gray-100"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Home
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>Review Extracted Data</h1>
                <p className="text-sm text-gray-600">Verify and edit the information extracted from your documents</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Step Indicator */}
          <div className="flex items-center justify-center space-x-4 mb-8">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-semibold">✓</div>
              <span className="text-sm font-medium text-gray-600">Upload</span>
            </div>
            <div className="w-16 h-0.5 bg-green-500"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-semibold">2</div>
              <span className="text-sm font-medium text-blue-600">Review & Edit</span>
            </div>
            <div className="w-16 h-0.5 bg-gray-300"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-gray-300 text-gray-600 flex items-center justify-center text-sm font-semibold">3</div>
              <span className="text-sm font-medium text-gray-400">Signature</span>
            </div>
            <div className="w-16 h-0.5 bg-gray-300"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-gray-300 text-gray-600 flex items-center justify-center text-sm font-semibold">4</div>
              <span className="text-sm font-medium text-gray-400">Download</span>
            </div>
          </div>

          {/* Important Notice */}
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-6">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <p className="font-semibold mb-1">Please Review Carefully</p>
                  <p>AI has extracted data from your documents. Please verify all fields for accuracy before proceeding. Edit any incorrect information.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Workflow Timer Display */}
          {extractionId && (
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="pt-6">
                <WorkflowTimer 
                  caseId={extractionId} 
                  onUpdate={(data) => setExtractionTiming(data)}
                />
              </CardContent>
            </Card>
          )}

          <Card className="border-slate-200 bg-white" data-testid="review-workflow-stage-card">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Current stage</p>
                  <p className="text-lg font-semibold text-gray-900" data-testid="review-workflow-stage-label">{uiStage}</p>
                  <p className="text-sm text-gray-600 mt-1">{uiStageMessage}</p>
                </div>
                {uiStage === REVIEW_STAGES.GENERATING_OPINION ? (
                  <div className="flex items-center gap-2 text-blue-700">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm font-medium">Generating opinion</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-sm font-medium">Extraction complete</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Bank Format Selection */}
          <Card data-testid="bank-format-card">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
              <CardTitle className="flex items-center space-x-2">
                <FileText className="w-5 h-5" />
                <span>Select Report Format</span>
              </CardTitle>
              <CardDescription>Choose the format for your legal opinion document (Retail, APF, or Legal Scrutiny)</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Lookback Period Info */}
              {formatRestrictions && formatRestrictions.lookback_years > 0 && (
                <div className={`mb-4 p-4 rounded-lg border ${
                  formatRestrictions.requires_user_choice 
                    ? 'bg-blue-50 border-blue-200' 
                    : 'bg-green-50 border-green-200'
                }`}>
                  <div className="flex items-start space-x-3">
                    <Info className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                      formatRestrictions.requires_user_choice ? 'text-blue-600' : 'text-green-600'
                    }`} />
                    <div className="text-sm">
                      <p className={`font-semibold mb-1 ${
                        formatRestrictions.requires_user_choice ? 'text-blue-900' : 'text-green-900'
                      }`}>
                        📅 Examination Period: {formatRestrictions.lookback_years} years
                      </p>
                      {formatRestrictions.requires_user_choice ? (
                        <p className="text-blue-800">
                          <strong>Retail format:</strong> Limited to 31 years (most recent) | 
                          <strong> APF format:</strong> Full {formatRestrictions.lookback_years} year examination
                        </p>
                      ) : (
                        <p className="text-green-800">All formats available for this examination period.</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Detection: {formatRestrictions.detection_method === 'ec_dates' ? 'EC Period dates' : 
                          formatRestrictions.detection_method === 'document_dates' ? 'Document registration dates' : 'Manual'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Retail Capped Acknowledgment Badge */}
              {formatRestrictions?.requires_user_choice && lookbackAcknowledged && isRetailFormat(selectedFormat) && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <CheckCircle className="w-4 h-4 inline mr-2 text-amber-600" />
                    <strong>Retail (31 years)</strong> - Opinion will cover most recent 31 years only. 
                    Documents prior to this period will not be examined.
                  </p>
                </div>
              )}
              
              <Select data-testid="bank-format-select" value={selectedFormat || ""} onValueChange={handleFormatSelect}>
                <SelectTrigger className="w-full h-12" data-testid="bank-format-select-trigger">
                  <SelectValue placeholder={bankFormats.length > 0 ? "Select a bank format..." : `No formats available`} />
                </SelectTrigger>
                <SelectContent>
                  {bankFormats.length === 0 && (
                    <div className="p-4 text-sm text-gray-500">
                      No bank formats available
                    </div>
                  )}
                  {bankFormats.map((format) => (
                    <SelectItem key={format.id} value={format.id} data-testid={`bank-format-option-${format.id}`}>
                      <span className="flex items-center">
                        {format.category === 'RETAIL' && <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>}
                        {format.category === 'APF' && <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>}
                        {format.category === 'SCRUTINY' && <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>}
                        {format.name} {format.wallet_cost && `(${format.wallet_cost} credit${format.wallet_cost > 1 ? 's' : ''})`}
                        {/* Show capped indicator for Retail when applicable */}
                        {format.category === 'RETAIL' && formatRestrictions?.requires_user_choice && (
                          <span className="ml-2 text-xs text-amber-600">(max 31 yrs)</span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Format Legend */}
              <div className="mt-3 flex items-center space-x-4 text-xs text-gray-500">
                <span className="flex items-center"><span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>Retail {formatRestrictions?.requires_user_choice ? '(max 31 yrs)' : ''}</span>
                <span className="flex items-center"><span className="w-2 h-2 bg-purple-500 rounded-full mr-1"></span>APF (full period)</span>
                <span className="flex items-center"><span className="w-2 h-2 bg-blue-500 rounded-full mr-1"></span>Scrutiny</span>
              </div>

              {formatPersisting && (
                <div className="mt-4 flex items-center gap-2 text-sm text-blue-700" data-testid="format-selection-saving-state">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving workflow selection...
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Lookback Choice Modal */}
          <Dialog open={showLookbackChoiceModal} onOpenChange={setShowLookbackChoiceModal}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  <span>Retail Opinion – Lookback Limitation</span>
                </DialogTitle>
                <DialogDescription className="text-left pt-4">
                  <span className="block mb-4">
                    Your document span is <strong>{formatRestrictions?.lookback_years} years</strong>.
                  </span>
                  <span className="block mb-4">
                    Retail format opinions are restricted to a maximum examination period of <strong>31 years</strong>. 
                    If you continue with Retail, the opinion will be issued only for the most recent 31 years.
                  </span>
                  <span className="block text-sm text-gray-600">
                    To cover the entire available period, please use the APF format.
                  </span>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="flex flex-col sm:flex-row gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => handleLookbackChoice('retail')}
                  className="flex-1"
                >
                  Continue with Retail (31 years)
                </Button>
                <Button 
                  onClick={() => handleLookbackChoice('apf')}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  ✓ Switch to APF (full period)
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {!hasSelectedFormat && (
            <Card className="border-dashed border-blue-300 bg-blue-50/70" data-testid="format-selection-first-notice">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3 text-sm text-blue-900">
                  <Info className="w-5 h-5 mt-0.5 text-blue-600" />
                  <div>
                    <p className="font-semibold">Format selection comes first</p>
                    <p className="mt-1">Choose a format to decide the workflow. Retail opens the Human Correction Gate. APF skips HCG and goes straight to APF generation.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {hasSelectedFormat && (
            <Card
              className={workflowType === 'apf' ? 'border-purple-200 bg-purple-50/60' : 'border-green-200 bg-green-50/60'}
              data-testid="workflow-branch-card"
            >
              <CardContent className="pt-6">
                {shouldShowAPFDirectFlow ? (
                  <div className="flex items-start gap-3 text-sm text-purple-900" data-testid="apf-direct-flow-banner">
                    <Zap className="w-5 h-5 mt-0.5 text-purple-600" />
                    <div>
                      <p className="font-semibold">APF workflow selected</p>
                      <p className="mt-1">Human Correction Gate is skipped for APF. You can generate the APF opinion directly from the extracted record.</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3 text-sm text-green-900" data-testid="retail-hcg-flow-banner">
                    <Edit3 className="w-5 h-5 mt-0.5 text-green-600" />
                    <div>
                      <p className="font-semibold">Retail workflow selected</p>
                      <p className="mt-1">Human Correction Gate is now active. Review and correct documents before generating the retail opinion.</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Drafting DNA Selection */}
          {hasSelectedFormat && (
          <Card data-testid="advocate-style-card" className="border-purple-200 bg-gradient-to-r from-purple-50/50 to-indigo-50/50">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <span>Drafting Standard</span>
                <span className="text-xs font-normal text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full ml-2">DNA</span>
              </CardTitle>
              <CardDescription>
                Apply an approved Drafting DNA during generation. Configure it in Settings → Advocate Profile.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <StyleSelector
                value={selectedStyleId}
                onChange={setSelectedStyleId}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground mt-3" data-testid="drafting-dna-runtime-note">
                Drafting DNA changes presentation only. Legal conclusions remain unchanged.
              </p>
            </CardContent>
          </Card>
          )}

          {/* Document-wise Review Section - HUMAN CORRECTION GATE */}
          {shouldShowHCG && (
          <DocumentWiseReview 
            extractionId={extractionId}
            onReviewReady={() => setReviewReady(true)}
            onApprovalStateChange={(state) => {
              if (!state) return;
              setHcgContract(state);
            }}
            onFieldsUpdated={(docs) => {
              // When documents are updated, store them for V2 generation
              if (docs && docs.length > 0) {
                setCorrectedDocuments(prevDocs => {
                  const prevSignature = JSON.stringify(prevDocs || []);
                  const nextSignature = JSON.stringify(docs || []);
                  if (prevSignature === nextSignature) {
                    return prevDocs;
                  }
                  console.log('📝 Documents updated from Human Correction Gate:', docs?.length || 0, 'documents');
                  return docs;
                });
              }
            }}
          />
          )}

          {/* Generation Timer Card - Shows during document generation */}
          {generating && generationStartTime && (
            <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">Generating Document</p>
                      <p className="text-sm text-gray-500">AI is creating your legal opinion...</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-purple-600">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-medium">Generation Time</span>
                    <GenerationTimer startTime={generationStartTime} />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Generate Button */}
          {hasSelectedFormat && (
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              {selectedFormat
                ? (shouldShowHCG
                    ? (hcgContract.isDirty
                        ? 'Save and approve HCG changes before generating.'
                        : (hcgContract.isApproved
                            ? `Selected ${selectedFormat}. Approved HCG v${hcgContract.corrected_documents_version || 1} is ready for generation.`
                            : 'Approve the Human Correction Gate before generating.'))
                    : `Selected ${selectedFormat}. Click Continue to Signature to generate or regenerate this format.`)
                : shouldShowHCG
                  ? (reviewReady ? 'Review documents above before generating' : 'Loading Human Correction Gate...')
                  : 'Select a format to continue.'}
            </p>
            <div className="flex items-center gap-3">
              <Button
                data-testid="continue-to-signature-button"
                onClick={handleGenerateOpinion}
                disabled={!canContinueToSignature}
                className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white px-8 py-6 text-lg font-semibold"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating...
                    <GenerationTimer startTime={generationStartTime} />
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Continue to Signature
                  </>
                )}
              </Button>
            </div>
          </div>
          )}
        </div>
      </main>
    </div>
  );
};

// =============================================================================
// DOCUMENT-WISE REVIEW COMPONENT (Embedded)
// Fetches registry from backend API — backend is the single source of truth
// =============================================================================

const RiskBadge = ({ risk }) => {
  // HCG status is intentionally binary: RED only for missing mandatory
  // registry.red fields; GREEN for everything else. No yellow/amber tier.
  const badges = {
    green: { icon: "✅", className: "text-green-500" },
    red: { icon: "🔴", className: "text-red-500" }
  };
  const badge = risk === 'red' ? badges.red : badges.green;
  return <span className={badge.className}>{badge.icon}</span>;
};

// Helper is declared INSIDE DocumentWiseReview so it can read the live
// registry (red-starred fields depend on document type, loaded from backend).
const DocumentWiseReview = ({ extractionId, onFieldsUpdated, onReviewReady, onApprovalStateChange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({ total_docs: 0, docs_with_issues: 0, critical_errors: 0, can_generate: true });
  const [documents, setDocuments] = useState([]);
  const [savingApproval, setSavingApproval] = useState(false);
  const [approvalMeta, setApprovalMeta] = useState({
    status: null,
    isApproved: false,
    corrected_documents_hash: null,
    corrected_documents_version: null,
    approved_at: null,
    approved_by: null,
    documents_count: 0,
    exceptions: [],
    reviewer_confirmation: false,
    isDirty: false,
  });
  const [activeDocIndex, setActiveDocIndex] = useState(0);
  const [files, setFiles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [imgZoom, setImgZoom] = useState(100);
  const [imgRotation, setImgRotation] = useState(0);
  const [showAddDocModal, setShowAddDocModal] = useState(false);
  const [showCustomDocInput, setShowCustomDocInput] = useState(false);
  const [customDocName, setCustomDocName] = useState('');
  const [customDocCategory, setCustomDocCategory] = useState('title');
  const [viewMode, setViewMode] = useState('smart'); // 'smart' | 'classic'

  // Registry state — fetched from backend API
  const [registry, setRegistry] = useState(null);
  const [categories, setCategories] = useState([]);
  const [aliases, setAliases] = useState({});
  const [fieldMeta, setFieldMeta] = useState({});
  const [visualGroups, setVisualGroups] = useState([]);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const lastPropagatedDocsRef = useRef('');
  
  // Get token for iframe authentication
  const token = localStorage.getItem('token');

  const emitDocumentsUpdate = useCallback((nextDocs) => {
    if (!onFieldsUpdated || !Array.isArray(nextDocs)) return;
    const signature = JSON.stringify(nextDocs);
    if (lastPropagatedDocsRef.current === signature) return;
    lastPropagatedDocsRef.current = signature;
    onFieldsUpdated(nextDocs);
  }, [onFieldsUpdated]);

  const emitApprovalState = useCallback((nextState) => {
    if (!onApprovalStateChange || !nextState) return;
    onApprovalStateChange(nextState);
  }, [onApprovalStateChange]);

  const unresolvedExceptions = useMemo(() => {
    return documents
      .map((doc) => {
        const resolvedDocType = registry?.[doc.document_type]
          ? doc.document_type
          : (aliases?.[doc.document_type] && registry?.[aliases[doc.document_type]])
            ? aliases[doc.document_type]
            : doc.document_type;
        const reg = registry?.[resolvedDocType] || registry?.OTHER || null;
        const requiredFields = Array.isArray(reg?.red) ? reg.red : [];
        const redRiskFields = Object.entries(doc.fields || {})
          .filter(([, fieldData]) => fieldData?.risk === 'red')
          .map(([fieldKey]) => fieldKey);
        const missingRequiredFields = requiredFields.filter((fieldKey) => {
          const value = doc.fields?.[fieldKey]?.value;
          return value === undefined || value === null || String(value).trim() === '';
        });
        const unresolvedFields = Array.from(new Set([...redRiskFields, ...missingRequiredFields]));
        if (!unresolvedFields.length) return null;
        return {
          document_type: doc.document_type,
          file_index: doc.file_index,
          unresolved_fields: unresolvedFields,
        };
      })
      .filter(Boolean);
  }, [documents, registry, aliases]);

  // ─── Resolve doc type through aliases ─────────────────────────────────────
  const resolveDocType = (docType) => {
    if (!docType || !registry) return docType;
    if (registry[docType]) return docType;
    const primary = aliases[docType];
    if (primary && registry[primary]) return primary;
    return docType;
  };

  // ─── Registry lookup ──────────────────────────────────────────────────────
  const getRegEntry = (docType) => {
    if (!registry) return null;
    const resolved = resolveDocType(docType);
    return registry[resolved] || registry['OTHER'] || null;
  };

  // ─── Document completeness — driven by RED-STAR fields from registry ──────
  // A doc is "complete" iff EVERY red-starred field (per its document_type)
  // has a non-empty value. The registry's red list is the single source of
  // truth — same list the UI renders with a red asterisk.
  const isDocumentComplete = (doc) => {
    if (!doc) return false;
    if (!doc.document_type) return false;
    const reg = getRegEntry(doc.document_type);
    const requiredFields = (reg && Array.isArray(reg.red)) ? reg.red : [];
    // If the registry hasn't loaded yet, fall back to "not complete" to avoid
    // a false-green flash during initial load.
    if (requiredFields.length === 0) return false;
    const f = doc.fields || {};
    return requiredFields.every((fk) => {
      const v = f[fk]?.value;
      return v !== undefined && v !== null && String(v).trim() !== "";
    });
  };

  const getDocCategory = (docType) => {
    const reg = getRegEntry(docType);
    return reg ? reg.category : 'legal';
  };

  const getDocName = (docType) => {
    const reg = getRegEntry(docType);
    return reg ? reg.name : (docType || 'Unknown');
  };

  // Resolve document_type to display name — handles both registry keys and display names
  const getDisplayType = (doc) => {
    const dtype = doc.document_type;
    if (!dtype) return 'Unknown';
    if (dtype === 'OTHER') return doc.custom_name || 'Other Document';
    return getDocName(dtype);
  };

  // Resolve document_type to registry key — for <select> value binding
  const getRegistryKey = (docType) => {
    if (!docType || !registry) return docType || '';
    // If it's already a registry key, return as-is
    if (registry[docType]) return docType;
    // If it's a display name, resolve via aliases
    const resolved = resolveDocType(docType);
    if (registry[resolved]) return resolved;
    return docType;
  };

  const getFieldLabel = (fieldKey, docType) => {
    // Per-doc-type override (ROLE_FIELDS style single source of truth).
    // Example: Gift Deed → vendor shows as "Donor"; GPA → executed_by
    // shows as "Principal"; LRS/BRS → authority shows as "Issuing Authority".
    if (docType) {
      const reg = getRegEntry(docType);
      const overrides = reg && reg.field_label_overrides;
      if (overrides && overrides[fieldKey]) return overrides[fieldKey];
    }
    if (fieldMeta[fieldKey]) return fieldMeta[fieldKey].label;
    return fieldKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const getFieldType = (fieldKey) => {
    if (fieldMeta[fieldKey]) return fieldMeta[fieldKey].type;
    return 'text';
  };

  const parseStructuredFieldValue = (value, fallback) => {
    if (Array.isArray(value) || (value && typeof value === 'object')) return value;
    if (typeof value !== 'string' || !value.trim()) return fallback;
    try {
      const parsed = JSON.parse(value);
      return parsed ?? fallback;
    } catch {
      return fallback;
    }
  };

  const getRepeatableRowsValue = (value) => {
    const parsed = parseStructuredFieldValue(value, []);
    return Array.isArray(parsed) ? parsed : [];
  };

  const getShareMatrixValue = (value) => {
    const parsed = parseStructuredFieldValue(value, []);
    return Array.isArray(parsed) ? parsed : [];
  };

  // ─── 2-TIER HCG STATUS MODEL ───────────────────────────────────────────────
  // Final rule: only missing mandatory registry.red fields are RED. Filled
  // fields and all optional fields are GREEN/neutral. No yellow/amber tier.
  const isBlank = (value) => (typeof value === 'string' ? value : String(value || '')).trim() === '';

  const isMandatoryField = (fieldName, docType) => {
    const reg = getRegEntry(docType);
    return !!(reg && Array.isArray(reg.red) && reg.red.includes(fieldName));
  };

  const getFieldRisk = (fieldName, value, docType) => {
    return isMandatoryField(fieldName, docType) && isBlank(value) ? 'red' : 'green';
  };

  const getDocStatus = (fields, docType) => {
    const reg = getRegEntry(docType);
    const requiredFields = (reg && Array.isArray(reg.red)) ? reg.red : [];
    const hasMissingMandatory = requiredFields.some((fieldName) => {
      const value = fields?.[fieldName]?.value;
      return isBlank(value);
    });
    return hasMissingMandatory ? 'red' : 'green';
  };

  const createEmptyDoc = (docType) => {
    const reg = getRegEntry(docType) || { fields: ["document_number", "document_date"], red: ["document_number", "document_date"] };
    const fields = {};
    (reg.fields || []).forEach(f => {
      const ftype = getFieldType(f);
      const initialValue = ['repeatable_party_rows', 'share_matrix'].includes(ftype) ? [] : '';
      fields[f] = { value: initialValue, risk: reg.red.includes(f) ? 'red' : 'green' };
    });
    return {
      document_type: docType,
      doc_status: reg.red.length > 0 ? 'red' : 'green',
      fields,
      page_range: null,
      _manually_added: true,
    };
  };

  const remapDocumentForTypeChange = (doc, nextDocType) => {
    const reg = getRegEntry(nextDocType) || { fields: [], red: [] };
    const nextFields = {};
    (reg.fields || []).forEach((fieldKey) => {
      const existing = doc.fields?.[fieldKey];
      const ftype = getFieldType(fieldKey);
      const emptyValue = ['repeatable_party_rows', 'share_matrix'].includes(ftype) ? [] : '';
      const value = existing?.value ?? emptyValue;
      nextFields[fieldKey] = {
        ...(existing || {}),
        value,
        risk: getFieldRisk(fieldKey, value, nextDocType),
      };
    });

    nextFields.document_type = {
      value: nextDocType,
      risk: 'green',
    };

    return {
      ...doc,
      document_type: nextDocType,
      fields: nextFields,
      doc_status: getDocStatus(nextFields, nextDocType),
    };
  };

  // ─── Group fields by visual group for layout ──────────────────────────────
  const getGroupedFields = (docType) => {
    const reg = getRegEntry(docType);
    if (!reg || !reg.fields) return [];
    const docFields = [...reg.fields];
    const activeDoc = documents?.[activeDocIndex];
    const resolvedType = resolveDocType(docType);
    if (activeDoc && resolveDocType(activeDoc.document_type) === resolvedType && resolvedType === 'EC') {
      ['ec_generated_date', 'ec_application_no', 'ec_from_date', 'ec_to_date'].forEach((extraField) => {
        if (activeDoc.fields?.[extraField] && !docFields.includes(extraField)) {
          const insertAt = docFields.indexOf('document_date');
          if (insertAt >= 0) {
            docFields.splice(insertAt + 1, 0, extraField);
          } else {
            docFields.push(extraField);
          }
        }
      });
    }
    const grouped = {};
    for (const f of docFields) {
      const meta = fieldMeta[f];
      const groupKey = meta ? meta.group : 'document_info';
      if (!grouped[groupKey]) grouped[groupKey] = [];
      grouped[groupKey].push(f);
    }
    // Return in visual group order
    return visualGroups
      .filter(vg => grouped[vg.key] && grouped[vg.key].length > 0)
      .map(vg => ({ key: vg.key, label: vg.label, fields: grouped[vg.key] }));
  };

  // ─── Fetch registry from backend API ──────────────────────────────────────
  useEffect(() => {
    const fetchRegistry = async () => {
      try {
        const res = await axios.get(`${API}/document-registry`);
        setRegistry(res.data.documents || {});
        setCategories(res.data.categories || []);
        setAliases(res.data.aliases || {});
        setFieldMeta(res.data.field_meta || {});
        setVisualGroups(res.data.visual_groups || []);
        // Build dropdown options from registry
        const cats = res.data.categories || [];
        const docs = res.data.documents || {};
        const opts = cats.map(cat => {
          const items = Object.entries(docs)
            .filter(([, cfg]) => cfg.category === cat.key)
            .map(([value, cfg]) => ({ value, text: cfg.name }));
          return { label: cat.label, items };
        }).filter(g => g.items.length > 0);
        setDropdownOptions(opts);
      } catch (err) {
        console.error('Failed to fetch document registry:', err);
      }
    };
    fetchRegistry();
  }, []);

  useEffect(() => {
    loadReviewData();
  }, [extractionId]);

  // Update current page when active document changes
  useEffect(() => {
    const activeDoc = documents[activeDocIndex];
    if (activeDoc && activeDoc.page_range) {
      setCurrentPage(activeDoc.page_range[0]);
    }
    setImgZoom(100);
    setImgRotation(0);
  }, [activeDocIndex]);
  // NOTE: documents removed from deps — it caused re-render loops when typing in Smart View

  const loadReviewData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/extraction/${extractionId}/review-documents`);
      
      if (response.data.success) {
        setSummary(response.data.summary || { total_docs: 0, docs_with_issues: 0, critical_errors: 0, can_generate: true });
        let loadedDocs = response.data.documents || [];
        const loadedHcg = response.data.hcg || {};
        
        // Recalculate risks using 3-tier model (RED/YELLOW/GREEN)
        loadedDocs = loadedDocs.map(doc => {
          const fields = doc.fields || {};
          // Recalculate each field's risk based on taxonomy
          const recalcFields = {};
          for (const [key, fieldData] of Object.entries(fields)) {
            const raw = fieldData.value;
            const val = (typeof raw === 'string' ? raw : String(raw || '')).trim();
            recalcFields[key] = { ...fieldData, risk: val ? 'green' : getFieldRisk(key, val, doc.document_type) };
          }
          return {
            ...doc,
            fields: recalcFields,
            doc_status: getDocStatus(recalcFields, doc.document_type),
            _original_type: doc._original_type || doc.document_type,  // Track original AI classification for correction indicator
          };
        });
        
        setDocuments(loadedDocs);
        setFiles(response.data.files || []);
        const nextApprovalMeta = {
          status: loadedHcg.status || null,
          isApproved: Boolean(loadedHcg.is_approved),
          corrected_documents_hash: loadedHcg.corrected_documents_hash || null,
          corrected_documents_version: loadedHcg.corrected_documents_version || null,
          approved_at: loadedHcg.approved_at || null,
          approved_by: loadedHcg.approved_by || null,
          documents_count: loadedHcg.documents_count || loadedDocs.length,
          exceptions: loadedHcg.exceptions || [],
          reviewer_confirmation: Boolean(loadedHcg.reviewer_confirmation),
          isDirty: false,
        };
        setApprovalMeta(nextApprovalMeta);
        emitApprovalState(nextApprovalMeta);
        queueMicrotask(() => onReviewReady && onReviewReady());
        
        // Set active doc to first one with errors
        const firstErrorIdx = loadedDocs.findIndex(d => d.doc_status === 'red');
        if (firstErrorIdx >= 0) {
          setActiveDocIndex(firstErrorIdx);
        }
      }
    } catch (err) {
      console.error('Error loading review data:', err);
      setError(err.response?.data?.detail || 'Failed to load document review');
      const failedApprovalMeta = {
        status: null,
        isApproved: false,
        corrected_documents_hash: null,
        corrected_documents_version: null,
        approved_at: null,
        approved_by: null,
        documents_count: 0,
        exceptions: [],
        reviewer_confirmation: false,
        isDirty: false,
      };
      setApprovalMeta(failedApprovalMeta);
      emitApprovalState(failedApprovalMeta);
      queueMicrotask(() => onReviewReady && onReviewReady());
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setApprovalMeta(prev => {
      const next = { ...prev, isDirty: true };
      emitApprovalState(next);
      return next;
    });
    setDocuments(prevDocs => {
      const newDocs = [...prevDocs];
      const doc = { ...newDocs[activeDocIndex] };
      const fields = { ...doc.fields };
      
      const risk = getFieldRisk(fieldName, value, doc.document_type);
      
      const existingField = fields[fieldName] || {};
      fields[fieldName] = { ...existingField, value: value, risk: risk };
      doc.fields = fields;
      doc.doc_status = getDocStatus(fields, doc.document_type);
      
      newDocs[activeDocIndex] = doc;
      return newDocs;
    });
  };

  const handleSaveAndApprove = async (approvalMode = 'approved') => {
    try {
      const hasUnresolved = unresolvedExceptions.length > 0;
      const requiresConfirmation = approvalMode === 'approved_with_exceptions' && hasUnresolved;
      if (hasUnresolved && approvalMode !== 'approved_with_exceptions') {
        toast.error('Unresolved red fields remain. Use Approve with Exceptions to proceed deliberately.');
        return;
      }
      if (requiresConfirmation) {
        const summary = unresolvedExceptions
          .map(item => `${item.document_type} (file ${item.file_index}): ${item.unresolved_fields.join(', ')}`)
          .join('\n');
        const confirmed = window.confirm(`Approve with exceptions and continue using this snapshot?\n\nUnresolved fields:\n${summary}`);
        if (!confirmed) return;
      }
      setSavingApproval(true);
      const response = await axios.put(`${API}/extraction/${extractionId}/corrected-documents`, {
        corrected_documents: documents,
        approval_mode: approvalMode,
        reviewer_confirmation: requiresConfirmation,
      });
      const nextHcg = response.data?.hcg || {};
      const nextApprovalMeta = {
        status: nextHcg.status || approvalMode,
        isApproved: Boolean(nextHcg.is_approved),
        corrected_documents_hash: nextHcg.corrected_documents_hash || null,
        corrected_documents_version: nextHcg.corrected_documents_version || null,
        approved_at: nextHcg.approved_at || null,
        approved_by: nextHcg.approved_by || null,
        documents_count: nextHcg.documents_count || documents.length,
        exceptions: nextHcg.exceptions || [],
        reviewer_confirmation: Boolean(nextHcg.reviewer_confirmation),
        isDirty: false,
      };
      setApprovalMeta(nextApprovalMeta);
      emitApprovalState(nextApprovalMeta);
      const label = nextApprovalMeta.status === 'approved_with_exceptions' ? 'approved with exceptions' : 'approved';
      toast.success(`HCG ${label} — version ${nextApprovalMeta.corrected_documents_version || 1}`);
    } catch (err) {
      console.error('Failed to save HCG corrections:', err);
      toast.error(err?.response?.data?.detail || 'Failed to save and approve HCG changes');
    } finally {
      setSavingApproval(false);
    }
  };

  useEffect(() => {
    if (!documents.length) return;
    emitDocumentsUpdate(documents);
  }, [documents, emitDocumentsUpdate]);

  const activeDoc = documents[activeDocIndex] || null;
  const activeFile = files.length > 0 ? files[activeDoc?.file_index || 0] : null;
  const pageRange = activeDoc?.page_range || [1, 1];

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-purple-600" />
          <p className="text-gray-600">Loading document review...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200">
        <CardContent className="py-8 text-center">
          <AlertTriangle className="w-8 h-8 mx-auto mb-4 text-red-500" />
          <p className="text-red-600">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (documents.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Info className="w-8 h-8 mx-auto mb-4 text-blue-500" />
          <p className="text-gray-600">No documents found for review.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="document-wise-review">
      <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              Document-wise Review
              <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full ml-2">
                HUMAN CORRECTION GATE
              </span>
            </CardTitle>
            <CardDescription className="mt-2">
              Review and correct extracted data document by document. Fields marked with * are required.
            </CardDescription>
          </div>
          {/* Summary Stats */}
          <div className="flex items-center gap-4 text-sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-800">{summary.total_docs}</div>
              <div className="text-xs text-gray-500">Documents</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-800">
                {summary.docs_with_issues}
              </div>
              <div className="text-xs text-gray-500">With Issues</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${
                documents.filter(d => !isDocumentComplete(d)).length > 0 ? 'text-red-600' : 'text-green-600'
              }`}>
                {documents.filter(d => !isDocumentComplete(d)).length}
              </div>
              <div className="text-xs text-gray-500">Incomplete</div>
            </div>
            <div className="min-w-[260px] rounded-lg border bg-white/80 px-3 py-2" data-testid="hcg-approval-status-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">HCG Status</p>
                  <p className={`text-sm font-semibold ${approvalMeta.isDirty ? 'text-amber-700' : approvalMeta.status === 'approved_with_exceptions' ? 'text-orange-700' : approvalMeta.isApproved ? 'text-green-700' : 'text-slate-700'}`} data-testid="hcg-approval-status-text">
                    {approvalMeta.isDirty ? 'Modified — save again' : approvalMeta.status === 'approved_with_exceptions' ? `Approved with exceptions v${approvalMeta.corrected_documents_version || 1}` : approvalMeta.isApproved ? `Approved v${approvalMeta.corrected_documents_version || 1}` : 'Pending approval'}
                  </p>
                  {approvalMeta.corrected_documents_hash && (
                    <p className="text-[11px] text-gray-500 mt-1" data-testid="hcg-approval-hash-text">
                      Hash {approvalMeta.corrected_documents_hash.slice(0, 10)}…
                    </p>
                  )}
                  {(approvalMeta.exceptions || []).length > 0 && (
                    <p className="text-[11px] text-orange-700 mt-1" data-testid="hcg-approval-exceptions-text">
                      {(approvalMeta.exceptions || []).length} document(s) approved with exceptions.
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    type="button"
                    size="sm"
                    data-testid="hcg-save-approve-button"
                    disabled={savingApproval || documents.length === 0 || unresolvedExceptions.length > 0}
                    onClick={() => handleSaveAndApprove('approved')}
                    className="bg-slate-900 text-white hover:bg-slate-800"
                  >
                    {savingApproval ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    {approvalMeta.isApproved && !approvalMeta.isDirty && approvalMeta.status !== 'approved_with_exceptions' ? 'Re-approve HCG' : 'Save & Approve'}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid="hcg-approve-with-exceptions-button"
                    disabled={savingApproval || documents.length === 0 || unresolvedExceptions.length === 0}
                    onClick={() => handleSaveAndApprove('approved_with_exceptions')}
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                  >
                    Approve with Exceptions
                  </Button>
                </div>
              </div>
              {unresolvedExceptions.length > 0 && (
                <div className="mt-3 rounded-md border border-orange-200 bg-orange-50/70 p-2 text-xs text-orange-900" data-testid="hcg-unresolved-summary">
                  <p className="font-semibold mb-1">Unresolved red fields</p>
                  <ul className="space-y-1 list-disc pl-4">
                    {unresolvedExceptions.map((item, idx) => (
                      <li key={`${item.document_type}-${item.file_index}-${idx}`}>
                        {item.document_type} (file {item.file_index}): {item.unresolved_fields.join(', ')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Document Tabs */}
        <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
          {documents.map((doc, idx) => {
            const complete = isDocumentComplete(doc);
            const isActive = idx === activeDocIndex;
            return (
              <button
                key={idx}
                onClick={() => setActiveDocIndex(idx)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex items-center gap-2
                  ${isActive
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'bg-white border hover:bg-gray-50 ' + (complete ? 'border-green-300 text-green-700' : 'border-red-300 text-red-700')
                  }
                `}
                data-testid={`doc-tab-${idx}`}
              >
                {getDisplayType(doc)}
                {doc._original_type && doc._original_type !== doc.document_type && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-normal" data-testid={`corrected-badge-${idx}`}>Corrected</span>
                )}
                <span className="text-xs opacity-70">#{idx + 1}</span>
              </button>
            );
          })}
          {/* Add Document — opens type selector */}
          <button
            data-testid="add-missing-record-btn"
            onClick={() => setShowAddDocModal(true)}
            className="px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap border-2 border-dashed border-gray-300 text-gray-500 hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-all flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Document
          </button>
        </div>
        
        {/* Add Document Modal */}
        {showAddDocModal && (
          <div className="mt-3 p-3 bg-white border-2 border-purple-200 rounded-lg shadow-sm">
            <p className="text-sm font-medium text-gray-700 mb-2">Select document type to add:</p>
            <div className="grid grid-cols-3 gap-2 max-h-[300px] overflow-y-auto">
              {dropdownOptions.map(group => (
                <div key={group.label}>
                  <p className="text-xs font-semibold text-gray-500 mb-1">{group.label}</p>
                  {group.items.map(item => (
                    <button
                      key={item.value}
                      onClick={() => {
                        const newDoc = createEmptyDoc(item.value);
                        setApprovalMeta(prev => {
                          const next = { ...prev, isDirty: true };
                          emitApprovalState(next);
                          return next;
                        });
                        setDocuments(prev => {
                          const updated = [...prev, newDoc];
                          return updated;
                        });
                        setActiveDocIndex(documents.length);
                        setShowAddDocModal(false);
                      }}
                      className="block w-full text-left text-sm px-2 py-1 rounded hover:bg-purple-50 hover:text-purple-700 transition-colors"
                    >
                      {item.text}
                    </button>
                  ))}
                </div>
              ))}
            </div>
            
            {/* Other / Unlisted Document */}
            {!showCustomDocInput ? (
              <button
                onClick={() => setShowCustomDocInput(true)}
                className="mt-2 w-full text-left text-sm px-2 py-2 rounded border border-dashed border-gray-300 text-gray-500 hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
              >
                + Other / Unlisted Document
              </button>
            ) : (
              <div className="mt-2 p-3 border rounded-lg bg-gray-50 space-y-2">
                <input
                  type="text"
                  placeholder="Document name (e.g., Village Proceedings 1998)"
                  value={customDocName}
                  onChange={(e) => setCustomDocName(e.target.value)}
                  className="w-full p-2 border rounded-md text-sm bg-white"
                  autoFocus
                />
                <div className="flex flex-wrap gap-3 text-sm">
                  {categories.map(cat => (
                    <label key={cat.key} className="flex items-center gap-1 cursor-pointer">
                      <input type="radio" name="customCat" value={cat.key} checked={customDocCategory === cat.key}
                        onChange={() => setCustomDocCategory(cat.key)} className="accent-purple-600" />
                      <span>{cat.label}</span>
                    </label>
                  ))}
                </div>
                <button
                  disabled={!customDocName.trim()}
                  onClick={() => {
                    const newDoc = {
                      document_type: "OTHER",
                      custom_name: customDocName.trim(),
                      doc_status: 'green',
                      fields: {
                        notes: { value: '', risk: 'green' },
                      },
                      page_range: null,
                      _manually_added: true,
                      _custom_category: customDocCategory,
                    };
                    setApprovalMeta(prev => {
                      const next = { ...prev, isDirty: true };
                      emitApprovalState(next);
                      return next;
                    });
                    setDocuments(prev => {
                      const updated = [...prev, newDoc];
                      return updated;
                    });
                    setActiveDocIndex(documents.length);
                    setShowAddDocModal(false);
                    setShowCustomDocInput(false);
                    setCustomDocName('');
                  }}
                  className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Add Document
                </button>
              </div>
            )}
            
            <button onClick={() => { setShowAddDocModal(false); setShowCustomDocInput(false); }} className="mt-2 text-xs text-gray-400 hover:text-gray-600">
              Cancel
            </button>
            <p className="text-xs text-gray-400 mt-1">Use if a document is missing or not listed above.</p>
          </div>
        )}
      </CardHeader>

      <CardContent className="pt-6">
        {activeDoc && (
          <div className="space-y-6">
            {/* Document Header */}
            <div className="flex items-center justify-between pb-4 border-b">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                {getDisplayType(activeDoc)}
              </h3>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">
                  Document {activeDocIndex + 1} of {documents.length}
                </span>
                {documents.length > 1 && (
                  <button
                    data-testid="delete-document-btn"
                    onClick={() => {
                      if (!window.confirm(`Remove "${getDisplayType(activeDoc)}" from review?`)) return;
                      setApprovalMeta(prev => {
                        const next = { ...prev, isDirty: true };
                        emitApprovalState(next);
                        return next;
                      });
                      setDocuments(prev => {
                        const updated = prev.filter((_, i) => i !== activeDocIndex);
                        return updated;
                      });
                      setActiveDocIndex(i => Math.min(i, documents.length - 2));
                    }}
                    className="px-2 py-1 text-xs text-red-500 border border-red-200 rounded hover:bg-red-50 hover:text-red-700 transition-colors"
                    title="Remove this document from review"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>

            {/* SPLIT VIEW: Document Preview (Left) + Field Editor (Right) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* LEFT: Document Preview */}
              <div className="border rounded-lg overflow-hidden bg-gray-100 flex flex-col sticky top-0" style={{ height: 'calc(100vh - 120px)', maxHeight: '800px' }}>
                <div className="bg-gray-800 text-white px-4 py-2 flex items-center justify-between flex-shrink-0">
                  <span className="text-sm font-medium">Original Document</span>
                  <span className="text-xs text-gray-300">
                    {files.length > 0 ? `File ${(activeDoc?.file_index || 0) + 1} of ${files.length}` : 'No files'}
                  </span>
                </div>
                
                {files.length > 0 && activeDoc?.page_range ? (
                  <>
                    {/* Image toolbar — zoom, rotate, fit controls */}
                    <div className="bg-gray-700 text-white px-3 py-1.5 flex items-center justify-center gap-3 flex-shrink-0 border-b border-gray-600">
                      <button onClick={() => setImgZoom(z => Math.max(z - 25, 25))} className="p-1 hover:bg-gray-600 rounded" title="Zoom Out">
                        <ZoomOut className="w-4 h-4" />
                      </button>
                      <span className="text-xs min-w-[40px] text-center">{imgZoom}%</span>
                      <button onClick={() => setImgZoom(z => Math.min(z + 25, 300))} className="p-1 hover:bg-gray-600 rounded" title="Zoom In">
                        <ZoomIn className="w-4 h-4" />
                      </button>
                      <div className="w-px h-4 bg-gray-500" />
                      <button onClick={() => setImgZoom(100)} className="p-1 hover:bg-gray-600 rounded" title="Fit to Width">
                        <Maximize className="w-4 h-4" />
                      </button>
                      <button onClick={() => setImgRotation(r => (r + 90) % 360)} className="p-1 hover:bg-gray-600 rounded" title="Rotate">
                        <RotateCw className="w-4 h-4" />
                      </button>
                      <div className="w-px h-4 bg-gray-500" />
                      <a href={`${API}/extraction/${extractionId}/file/${activeDoc?.file_index || 0}/preview?token=${token}`}
                         target="_blank" rel="noopener noreferrer"
                         className="text-xs text-blue-300 hover:text-blue-200 underline">
                        Open in new tab
                      </a>
                    </div>
                    
                    {/* Document content — scrollable with zoom/rotate applied */}
                    <div className="flex-1 overflow-auto bg-gray-200" style={{ position: 'relative' }}>
                      <div style={{ 
                        transform: `scale(${imgZoom / 100}) rotate(${imgRotation}deg)`,
                        transformOrigin: 'top left',
                        transition: 'transform 0.2s ease',
                        width: imgZoom === 100 ? '100%' : undefined,
                      }}>
                        <iframe
                          src={`${API}/extraction/${extractionId}/file/${activeDoc?.file_index || 0}/preview?token=${token}#page=${currentPage}`}
                          className="w-full border-0"
                          title="Document Preview"
                          style={{ height: 'calc(100vh - 200px)', minHeight: '400px' }}
                        />
                      </div>
                    </div>
                    
                    {/* Page Navigation */}
                    <div className="bg-gray-800 text-white px-4 py-2 flex items-center justify-between flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(p - 1, pageRange[0]))}
                        disabled={currentPage <= pageRange[0]}
                        className="text-white hover:bg-gray-700"
                      >
                        ◀ Prev
                      </Button>
                      <div className="flex flex-col items-center">
                        <span className="text-sm">
                          Page {currentPage} (Range: {pageRange[0]}–{pageRange[1]})
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(p + 1, pageRange[1]))}
                        disabled={currentPage >= pageRange[1]}
                        className="text-white hover:bg-gray-700"
                      >
                        Next ▶
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-gray-500" style={{ minHeight: '500px' }}>
                    <div className="text-center">
                      <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>{activeDoc?._manually_added ? 'Manually added record — no source document' : 'No document preview available'}</p>
                      {activeDoc?._manually_added && (
                        <p className="text-xs text-gray-400 mt-1">Fill in the fields on the right to complete this record</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* RIGHT: Manual correction form (Smart View removed — manual input only) */}
              <div>
              <div className="space-y-4 max-h-[700px] overflow-y-auto">
                {/* Document Type Dropdown - Special handling */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b">
                    <h4 className="font-medium text-gray-700">
                      📌 Document Type
                    </h4>
                  </div>
                  <div className="p-4">
                    <Label className="text-sm text-gray-600 flex items-center gap-1 mb-2">
                      Document Type
                      <span className="text-green-500">✓</span>
                    </Label>
                    <select
                      value={getRegistryKey(activeDoc.document_type)}
                      onChange={(e) => {
                        setApprovalMeta(prev => {
                          const next = { ...prev, isDirty: true };
                          emitApprovalState(next);
                          return next;
                        });
                        setDocuments(prevDocs => {
                          const newDocs = [...prevDocs];
                          newDocs[activeDocIndex] = remapDocumentForTypeChange(newDocs[activeDocIndex], e.target.value);
                          return newDocs;
                        });
                      }}
                      className="w-full p-2 border rounded-md bg-white text-gray-900"
                      data-testid="doc-type-dropdown"
                    >
                      {dropdownOptions.map(group => (
                        <optgroup key={group.label} label={group.label}>
                          {group.items.map(item => (
                            <option key={item.value} value={item.value}>{item.text}</option>
                          ))}
                        </optgroup>
                      ))}
                      <optgroup label="Other">
                        <option value="OTHER">Other / Unlisted Document</option>
                      </optgroup>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Change if AI misclassified the document type
                    </p>
                    {getDocCategory(activeDoc.document_type) === 'revenue' && (
                      <p className="text-xs text-amber-600 mt-2 bg-amber-50 px-2 py-1 rounded">
                        Revenue records reflect possession/cultivation, not legal ownership.
                      </p>
                    )}
                    {getDocCategory(activeDoc.document_type) === 'approvals' && (
                      <p className="text-xs text-blue-600 mt-2 bg-blue-50 px-2 py-1 rounded">
                        Permissions and approvals are supporting documents — they do not establish title.
                      </p>
                    )}
                  </div>
                </div>

                {/* Field groups — driven by registry.fields + visual grouping */}
                {getGroupedFields(activeDoc.document_type).map(group => (
                  <div key={group.key} className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 border-b">
                      <h4 className="font-medium text-gray-700">
                        {group.label}
                      </h4>
                    </div>
                    <div className="p-4 grid gap-4">
                      {group.fields.map(fieldKey => {
                        const fieldData = activeDoc.fields[fieldKey] || { value: '', risk: getFieldRisk(fieldKey, '', activeDoc.document_type) };
                        const label = getFieldLabel(fieldKey, activeDoc.document_type);
                        const reg = getRegEntry(activeDoc.document_type);
                        const isCritical = reg ? reg.red.includes(fieldKey) : false;
                        const ftype = getFieldType(fieldKey);

                        // Special handling for GPA toggle (boolean type)
                        if (fieldKey === 'executed_via_gpa') {
                          return (
                            <div key={fieldKey} className="space-y-1">
                              <Label className="text-sm text-gray-600 flex items-center gap-1">
                                {label}
                                {isCritical && <span className="text-red-500">*</span>}
                              </Label>
                              <select
                                value={fieldData.value || 'No'}
                                onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                                className="w-full p-2 border rounded-md bg-white text-gray-900"
                              >
                                <option value="No">No - Direct execution by owner</option>
                                <option value="Yes">Yes - Executed by GPA holder</option>
                              </select>
                              <p className="text-xs text-gray-500">
                                Was this deed executed by a Power of Attorney holder on behalf of the owner?
                              </p>
                            </div>
                          );
                        }

                        // GPA doc number — conditional on executed_via_gpa = Yes
                        if (fieldKey === 'gpa_doc_number') {
                          const gpaFlag = activeDoc.fields['executed_via_gpa']?.value;
                          if (gpaFlag !== 'Yes') return null;
                        }

                        if (ftype === 'repeatable_party_rows') {
                          const rows = getRepeatableRowsValue(fieldData.value);
                          const updateRow = (rowIndex, key, nextValue) => {
                            const nextRows = rows.map((row, idx) => (
                              idx === rowIndex ? { ...row, [key]: nextValue } : row
                            ));
                            handleFieldChange(fieldKey, nextRows);
                          };

                          return (
                            <div key={fieldKey} className="space-y-3">
                              <div className="flex items-center justify-between">
                                <Label className="text-sm text-gray-600 flex items-center gap-1">
                                  {label}
                                  {isCritical && <span className="text-red-500">*</span>}
                                </Label>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  data-testid={`${fieldKey}-add-row-button`}
                                  onClick={() => handleFieldChange(fieldKey, [...rows, {
                                    name: '',
                                    father_or_spouse_name: '',
                                    address: '',
                                    share_percentage: '',
                                  }])}
                                >
                                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Co-owner
                                </Button>
                              </div>

                              {rows.length === 0 && (
                                <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-4 text-sm text-gray-500" data-testid={`${fieldKey}-empty-state`}>
                                  No co-owner rows yet. Add each co-owner with relation, address, and share percentage.
                                </div>
                              )}

                              {rows.map((row, rowIndex) => (
                                <div key={`${fieldKey}-${rowIndex}`} className="rounded-lg border bg-white p-3 space-y-3" data-testid={`${fieldKey}-row-${rowIndex}`}>
                                  <div className="flex items-center justify-between">
                                    <p className="text-sm font-medium text-gray-700">Co-owner {rowIndex + 1}</p>
                                    <button
                                      type="button"
                                      className="text-xs text-red-500 hover:text-red-700"
                                      data-testid={`${fieldKey}-remove-row-${rowIndex}`}
                                      onClick={() => handleFieldChange(fieldKey, rows.filter((_, idx) => idx !== rowIndex))}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  <div className="grid gap-3 md:grid-cols-2">
                                    <Input
                                      value={row.name || ''}
                                      onChange={(e) => updateRow(rowIndex, 'name', e.target.value)}
                                      placeholder="Co-owner name"
                                      data-testid={`${fieldKey}-name-${rowIndex}`}
                                    />
                                    <Input
                                      value={row.father_or_spouse_name || ''}
                                      onChange={(e) => updateRow(rowIndex, 'father_or_spouse_name', e.target.value)}
                                      placeholder="Father / spouse name"
                                      data-testid={`${fieldKey}-relation-${rowIndex}`}
                                    />
                                  </div>
                                  <Textarea
                                    value={row.address || ''}
                                    onChange={(e) => updateRow(rowIndex, 'address', e.target.value)}
                                    placeholder="Address"
                                    className="min-h-[88px]"
                                    data-testid={`${fieldKey}-address-${rowIndex}`}
                                  />
                                  <Input
                                    value={row.share_percentage || ''}
                                    onChange={(e) => updateRow(rowIndex, 'share_percentage', e.target.value)}
                                    placeholder="Share percentage"
                                    data-testid={`${fieldKey}-share-${rowIndex}`}
                                  />
                                </div>
                              ))}
                            </div>
                          );
                        }

                        if (ftype === 'share_matrix') {
                          const matrixRows = getShareMatrixValue(fieldData.value);
                          const updateRow = (rowIndex, key, nextValue) => {
                            const nextRows = matrixRows.map((row, idx) => (
                              idx === rowIndex ? { ...row, [key]: nextValue } : row
                            ));
                            handleFieldChange(fieldKey, nextRows);
                          };

                          return (
                            <div key={fieldKey} className="space-y-3">
                              <div className="flex items-center justify-between">
                                <Label className="text-sm text-gray-600 flex items-center gap-1">
                                  {label}
                                  {isCritical && <span className="text-red-500">*</span>}
                                </Label>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  data-testid={`${fieldKey}-add-row-button`}
                                  onClick={() => handleFieldChange(fieldKey, [...matrixRows, { co_owner_name: '', share_percentage: '' }])}
                                >
                                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Share Row
                                </Button>
                              </div>

                              {matrixRows.length === 0 && (
                                <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-4 text-sm text-gray-500" data-testid={`${fieldKey}-empty-state`}>
                                  Add the co-owner share split here if the deed states an explicit matrix.
                                </div>
                              )}

                              {matrixRows.map((row, rowIndex) => (
                                <div key={`${fieldKey}-${rowIndex}`} className="grid gap-3 md:grid-cols-[1fr_180px_auto] items-center rounded-lg border bg-white p-3" data-testid={`${fieldKey}-row-${rowIndex}`}>
                                  <Input
                                    value={row.co_owner_name || ''}
                                    onChange={(e) => updateRow(rowIndex, 'co_owner_name', e.target.value)}
                                    placeholder="Co-owner name"
                                    data-testid={`${fieldKey}-name-${rowIndex}`}
                                  />
                                  <Input
                                    value={row.share_percentage || ''}
                                    onChange={(e) => updateRow(rowIndex, 'share_percentage', e.target.value)}
                                    placeholder="Share percentage"
                                    data-testid={`${fieldKey}-percentage-${rowIndex}`}
                                  />
                                  <button
                                    type="button"
                                    className="text-xs text-red-500 hover:text-red-700"
                                    data-testid={`${fieldKey}-remove-row-${rowIndex}`}
                                    onClick={() => handleFieldChange(fieldKey, matrixRows.filter((_, idx) => idx !== rowIndex))}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                            </div>
                          );
                        }

                        if (ftype === 'textarea') {
                          return (
                            <div key={fieldKey} className="space-y-1">
                              <Label className="text-sm text-gray-600 flex items-center gap-1">
                                {label}
                                {isCritical && <span className="text-red-500">*</span>}
                              </Label>
                              <Textarea
                                value={fieldData.value || ''}
                                onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                                placeholder={`Enter ${label.toLowerCase()}`}
                                className="min-h-[110px]"
                                data-testid={`${fieldKey}-textarea`}
                              />
                            </div>
                          );
                        }

                        return (
                          <div key={fieldKey} className="space-y-1">
                            <Label className="text-sm text-gray-600 flex items-center gap-1">
                              {label}
                              {isCritical && <span className="text-red-500">*</span>}
                            </Label>
                            <Input
                              type={ftype === 'number' ? 'text' : (ftype === 'date' ? 'text' : 'text')}
                              value={fieldData.value || ''}
                              onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                              placeholder={`Enter ${label.toLowerCase()}`}
                              data-testid={`${fieldKey}-input`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>  {/* End of right panel */}
            </div>  {/* End of grid */}
            {/* Navigation */}
            <div className="flex justify-between items-center pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setActiveDocIndex(Math.max(0, activeDocIndex - 1))}
                disabled={activeDocIndex === 0}
              >
                ← Previous
              </Button>
              <span className="text-sm text-gray-500">
                {activeDocIndex + 1} / {documents.length}
              </span>
              <Button
                variant="outline"
                onClick={() => setActiveDocIndex(Math.min(documents.length - 1, activeDocIndex + 1))}
                disabled={activeDocIndex === documents.length - 1}
              >
                Next →
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExtractPage;